import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { TextingTab as TextingTabComp } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress'; // eslint-disable-line max-len
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { useChatContainer } from './hooks';
import { conversationToComponentConversation } from './mappings';
import ThreadHeader from './ThreadHeader';
import intlMessages from '../messages';

type Props = {
  selectedConversation: ?Conversation,
  onStatusChange: () => void,
  updateConversationLocally: (conversationId: string, updates: Object) => void,
  intl: any,
};

function ChatContainer({
  selectedConversation,
  onStatusChange,
  updateConversationLocally,
  intl,
}: Props): React$Node {
  const {
    messages,
    hasMoreMessages,
    message,
    isSending,
    textingPhoneNumber,
    isLoading,
    isPropertyLoading,
    messageMaxLength,
    infoOptedIn,
    handleLoadMoreMessages,
    handleMessageChange,
    handleSendMessage,
    handleToggleResolved,
    replyingToMessage,
    handleReplyToMessage,
    handleCancelReply,
  } = useChatContainer({
    selectedConversation,
    onStatusChange,
    updateConversationLocally,
    intl,
  });

  // Convert MessagingHub conversation to TextingTab conversation format
  const componentConversation = useMemo(
    () =>
      selectedConversation
        ? conversationToComponentConversation(selectedConversation)
        : null,
    [selectedConversation],
  );

  if (!selectedConversation) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Typography variant="bodyMedium" color="text.secondary">
          {intl.formatMessage(intlMessages.selectConversation)}
        </Typography>
      </Box>
    );
  }

  if (isPropertyLoading || (isLoading && messages.length === 0)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <ThreadHeader conversation={selectedConversation} />
      <Box
        p={2}
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <TextingTabComp
          intl={intl}
          conversationHeight={600}
          conversations={componentConversation ? [componentConversation] : []}
          messages={messages}
          hasMoreMessages={hasMoreMessages}
          selectedConversationId={selectedConversation.conversationId}
          message={message}
          isSending={isSending}
          textingPhoneNumber={textingPhoneNumber}
          messageMaxLength={messageMaxLength}
          onLoadMoreMessages={handleLoadMoreMessages}
          onSelectConversation={() => {}}
          onMessageChange={handleMessageChange}
          onSendMessage={handleSendMessage}
          onToggleResolved={handleToggleResolved}
          hideHeader
          hideMessagesColumn
          isOptedIn={infoOptedIn}
          replyingToMessage={replyingToMessage}
          onReplyToMessage={handleReplyToMessage}
          onCancelReply={handleCancelReply}
        />
      </Box>
    </Box>
  );
}

export default (injectIntl(ChatContainer): any);
